import React from "react";

const ThreePWorkers = () => {
  return (
    <div className="bg-black text-white [&_p]:text-white [&_li]:text-gray-200 min-h-screen p-4">

      <div className="mx-auto max-w-4xl">

        <h1 className="text-lg lg:text-2xl font-bold text-yellow-200 mb-4">
          🧑‍🏭 3P for Workers
        </h1>

        <h2 className="text-sm lg:text-lg text-purple-300 mb-6">
          Story: Suresh and the “20 Motors” Guess
        </h2>

        <p className="text-gray-200 mb-4">
          It is 8:55 AM on the factory floor. The machines are warming up, the
          assembly tables are ready, and workers are putting on their safety
          gloves.
        </p>

        <p className="text-gray-200 mb-4">
          Suresh is an experienced machine operator responsible for assembling
          motor units.
        </p>

        <p className="text-gray-200 mb-4">
          Just before the shift starts, the supervisor walks over and asks:
        </p>

        <p className="text-yellow-200 mb-4">
          “Suresh, how many motors can you assemble today?”
        </p>

        <p className="text-gray-200 mb-4">
          Suresh quickly thinks for a moment and replies confidently:
        </p>

        <p className="text-yellow-200 mb-6">
          “Maybe 20 motors.”
        </p>

        <p className="text-gray-200 mb-6">
          The supervisor writes the number on the production board. Now the
          entire day’s production plan is based on this estimate.
        </p>

        <h3 className="text-purple-300 text-lg font-semibold mb-3">
          The Shift Begins
        </h3>

        <ul className="list-disc list-inside text-gray-200 space-y-1 mb-6">
          <li>Motor 1 — Done</li>
          <li>Motor 2 — Done</li>
          <li>Motor 3 — Done</li>
        </ul>

        <p className="text-gray-200 mb-4">
          But soon the small problems begin.
        </p>

        <p className="text-gray-200 mb-4">
          Motor 5 takes longer than expected because a shaft is slightly
          misaligned. Later, during Motor 8 assembly, Suresh realizes a tool
          needs tightening and calibration.
        </p>

        <p className="text-gray-200 mb-4">
          After lunch another issue appears. Motor 12 fails the quality test and
          must be disassembled and reassembled.
        </p>

        <p className="text-gray-200 mb-6">
          By the end of the shift, the production counter shows only:
        </p>

        <p className="text-yellow-200 text-lg mb-6">
          15 motors completed.
        </p>

        <h3 className="text-purple-300 text-lg font-semibold mb-3">
          The Hidden Problem
        </h3>

        <p className="text-gray-200 mb-4">
          The supervisor compares the numbers.
        </p>

        <p className="text-yellow-200 mb-4">
          “You estimated 20 motors… but finished 15.”
        </p>

        <p className="text-gray-200 mb-4">
          The supervisor assumes the worker underperformed.
        </p>

        <p className="text-gray-200 mb-4">
          But the real problem was something else.
        </p>

        <p className="text-yellow-200 mb-6">
          The estimate itself was wrong.
        </p>

        <ul className="list-disc list-inside text-gray-200 space-y-1 mb-6">
          <li>production expectations become unrealistic</li>
          <li>workers feel unnecessary pressure</li>
          <li>supervisors misjudge performance</li>
          <li>factory planning becomes inaccurate</li>
        </ul>

        <h3 className="text-purple-300 text-lg font-semibold mb-3">
          The Planning Skill Workers Rarely Learn
        </h3>

        <p className="text-gray-200 mb-6">
          During a productivity improvement session, the factory introduces a
          simple concept used in professional project planning.
        </p>

        <p className="text-yellow-200 text-lg mb-6">
          Think5Whys PTM 3P Estimation
        </p>

        <p className="text-gray-200 mb-6">
          Instead of guessing productivity, workers consider three realistic
          scenarios.
        </p>

        <h3 className="text-purple-300 text-lg font-semibold mb-3">
          How Suresh Could Estimate His Productivity
        </h3>

        <div className="bg-gray-900 p-4 rounded mb-6">

          <table className="w-full text-left text-sm lg:text-base">
            <thead className="text-yellow-200">
              <tr>
                <th>Estimate</th>
                <th>Motors / Hour</th>
              </tr>
            </thead>

            <tbody className="text-gray-200">
              <tr>
                <td>Optimistic (O)</td>
                <td>6</td>
              </tr>

              <tr>
                <td>Most Likely (M)</td>
                <td>5</td>
              </tr>

              <tr>
                <td>Pessimistic (P)</td>
                <td>3</td>
              </tr>
            </tbody>

          </table>

        </div>

        <ul className="list-disc list-inside text-gray-200 space-y-1 mb-6">
          <li>If everything runs smoothly → 6 motors/hour</li>
          <li>Normal conditions → 5 motors/hour</li>
          <li>If delays occur → 3 motors/hour</li>
        </ul>

        <h3 className="text-purple-300 text-lg font-semibold mb-3">
          Think5Whys PTM 3P Calculation
        </h3>

        <p className="text-yellow-200 mb-3">Triangular Estimate</p>

        <p>E = (O + M + P)/3</p>

        <p className="text-gray-200 mt-4 mb-6">
          With values (6 + 5 + 3) / 3 ≈ 4.67 motors per hour
        </p>

        <p className="text-yellow-200 mb-3">PERT Estimate</p>

        <p>E = (O + 4M + P)/6</p>

        <p className="text-gray-200 mt-4 mb-6">
          (6 + 4×5 + 3) / 6 ≈ 4.83 motors per hour
        </p>

        <h3 className="text-purple-300 text-lg font-semibold mb-3">
          Realistic Daily Planning
        </h3>

        <p className="text-gray-200 mb-4">
          If the factory shift is 8 hours:
        </p>

        <p className="text-yellow-200 mb-6">
          4.8 × 8 ≈ 38 motors
        </p>

        <p className="text-gray-200 mb-6">
          Now production planning becomes data-driven instead of guesswork.
        </p>

        <h3 className="text-purple-300 text-lg font-semibold mb-3">
          What Changes for the Worker
        </h3>

        <ul className="list-disc list-inside text-gray-200 space-y-1 mb-6">
          <li>productivity targets become realistic</li>
          <li>supervisors understand real working conditions</li>
          <li>pressure and blame reduce</li>
          <li>planning becomes fair and transparent</li>
        </ul>

        <h3 className="text-purple-300 text-lg font-semibold mb-3">
          Using 3P in Daily Factory Work
        </h3>

        <ul className="list-disc list-inside text-gray-200 space-y-1 mb-6">
          <li>estimating assembly time</li>
          <li>planning machine setup</li>
          <li>predicting maintenance work</li>
          <li>estimating production batches</li>
          <li>scheduling shift workloads</li>
        </ul>

        <h3 className="text-purple-300 text-lg font-semibold mb-3">
          ⭐ The Core Message
        </h3>

        <p className="text-gray-200 mb-4">
          Without Three Point Estimation, people rely on guessing.
        </p>

        <ul className="list-disc list-inside text-gray-200 space-y-1 mb-6">
          <li>delays</li>
          <li>stress</li>
          <li>poor planning</li>
          <li>lost opportunities</li>
        </ul>

        <p className="text-gray-200 mb-4">
          With Think5Whys PTM 3P:
        </p>

        <ul className="list-disc list-inside text-gray-200 space-y-1 mb-6">
          <li>better decisions</li>
          <li>realistic planning</li>
          <li>improved productivity</li>
          <li>stronger trust in teams</li>
        </ul>

        <p className="text-yellow-200 text-lg">
          The difference between guessing and estimating is often the difference
          between chaos and control.
        </p>

      </div>
    </div>
  );
};

export default ThreePWorkers;